import { redirect } from "@tanstack/react-router";
import {
  currentPersonQueryOptions,
  PersonAccessError,
  SessionExpiredError,
} from "@/hooks/use-current-person";
import { authClient } from "./auth-client";
import { queryClient } from "./query";
import { returnPathOf } from "./return-path";

type PersonCache = Pick<typeof queryClient, "ensureQueryData">;

type AppSession = NonNullable<Awaited<ReturnType<typeof authClient.getSession>>["data"]>;

/** One session read: Better Auth answers `{ data, error }` and may throw on a network failure. */
interface SessionRead {
  data: AppSession | null;
  error?: { status?: number } | null;
}

/** The session could not be checked (rate limited, server or network trouble); not signed out. */
export class SessionCheckError extends Error {
  constructor() {
    super("We couldn't check your sign-in. Try again.");
    this.name = "SessionCheckError";
  }
}

/** Where a page was opened, so sign-in can come back to it (a shared task link). */
type PageLocation = { pathname: string; searchStr: string };

/** To /login, holding the page to return to. */
function toLogin(location: PageLocation | undefined) {
  const back = location ? returnPathOf(location) : undefined;
  return redirect({ to: "/login", search: back ? { redirect: back } : {} });
}

/** Waits before each retry of a failed session read; after the last, the error shows. */
const SESSION_RETRY_MS = [500, 1500];
const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * The signed-in session, or a redirect to /login only on a real "no session" (an empty answer
 * or a 401). A 429, a 5xx or a network failure is retried, then thrown as SessionCheckError,
 * which the router shows with a Try again (RouteError): a busy moment must not sign anyone out
 * (DECISIONS 2026-10-15).
 */
export async function requireAuth(
  location?: PageLocation,
  getSession: () => Promise<SessionRead> = () => authClient.getSession(),
  sleep: (ms: number) => Promise<void> = wait,
): Promise<AppSession> {
  for (let attempt = 0; ; attempt++) {
    let read: SessionRead | null = null;
    try {
      read = await getSession();
    } catch {
      read = null;
    }
    if (read && !read.error) {
      if (!read.data) throw toLogin(location);
      return read.data;
    }
    if (read?.error?.status === 401) throw toLogin(location);
    const delay = SESSION_RETRY_MS[attempt];
    if (delay === undefined) throw new SessionCheckError();
    await sleep(delay);
  }
}

type SessionGetter = () => Promise<{ data: unknown }>;

/** A visitor with a session goes to /dashboard; without one, to /login. */
export async function redirectHome(getSession: SessionGetter = () => authClient.getSession()) {
  const session = await getSession();
  throw redirect({ to: session.data ? "/dashboard" : "/login" });
}

/**
 * The login page is for signed-out visitors; a session goes straight to `returnPath`, which the
 * /login route has already checked, or /dashboard.
 */
export async function redirectIfSignedIn(
  returnPath?: string,
  getSession: SessionGetter = () => authClient.getSession(),
) {
  const session = await getSession();
  if (!session.data) return;
  throw returnPath ? redirect({ href: returnPath }) : redirect({ to: "/dashboard" });
}

export async function requireAdmin() {
  const session = await requireAuth();
  if (session.user.role !== "admin") throw redirect({ to: "/dashboard" });
  return session;
}

/**
 * The signed-in user must be one active warehouse Person; anyone else lands on /no-access.
 * An admin without a Person (the local admin@ seed) may still open Settings.
 */
export async function requirePerson(
  role: string | null | undefined,
  location: PageLocation,
  client: PersonCache = queryClient,
) {
  try {
    await client.ensureQueryData(currentPersonQueryOptions);
  } catch (err) {
    if (err instanceof SessionExpiredError) throw toLogin(location);
    if (!(err instanceof PersonAccessError)) throw err;
    if (role === "admin" && location.pathname.startsWith("/dashboard/settings")) return;
    throw redirect({ to: "/no-access" });
  }
}
